// ============================================================
// C2：高清出图（导出图片）按钮 + 弹窗
// 分辨率 1080p/2K/4K × 格式 PNG/JPEG × 视角；免费档自动加水印
// ============================================================
import { useState } from 'react';
import { Button, Modal, Radio, Select, Space, Tooltip, Typography, message } from 'antd';
import { PictureOutlined } from '@ant-design/icons';
import useSceneStore from '../../stores/sceneStore';
import { sceneImageExportRegistry } from '../../stores/sceneImageExportStore';
import { EXPORT_PRESETS, EXPORT_VIEWS, buildExportFilename, downloadDataUrl, stampWatermark } from '../../utils/imageExport';
import { recordRenderExport } from '../../api/renderExports';
import { useWatermarkFlag } from '../Watermark';

export default function ExportImageButton() {
  const [open, setOpen] = useState(false);
  const [presetKey, setPresetKey] = useState('fhd');
  const [format, setFormat] = useState<'png' | 'jpeg'>('png');
  const [viewKey, setViewKey] = useState('current');
  const [exporting, setExporting] = useState(false);
  const watermarkFlag = useWatermarkFlag();
  const scene = useSceneStore((s) => s.currentScene);

  const doExport = async () => {
    const preset = EXPORT_PRESETS.find((p) => p.key === presetKey) ?? EXPORT_PRESETS[0];
    const view = EXPORT_VIEWS.find((v) => v.key === viewKey) ?? EXPORT_VIEWS[0];
    if (!sceneImageExportRegistry.capture) {
      message.error('画布未就绪（请在 3D 编辑器中重试）');
      return;
    }
    setExporting(true);
    try {
      const raw = sceneImageExportRegistry.capture({
        width: preset.width,
        height: preset.height,
        format,
        quality: 0.92,
        ...(view.position && view.target ? { view: { position: view.position, target: view.target } } : {}),
      });
      if (!raw) {
        message.error('导出失败（WebGL 画布不可用）');
        return;
      }
      let out = raw;
      if (watermarkFlag) {
        out = await stampWatermark(raw, { text: 'DTwince', logoUrl: '/watermark-logo.png' }).catch(() => raw);
      }
      const filename = buildExportFilename(scene?.name ?? '场景', preset.width, preset.height, format);
      downloadDataUrl(out, filename);
      // 留痕（计费/审计基础；失败不影响导出）
      void recordRenderExport({
        ...(scene?.id ? { sceneId: scene.id } : {}),
        width: preset.width,
        height: preset.height,
        format,
        ...(view.key !== 'current' ? { view: view.key } : {}),
        bytes: out.length,
      }).catch(() => undefined);
      message.success(`已导出 ${filename}${watermarkFlag ? '（含水印）' : ''}`);
      setOpen(false);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Tooltip title="导出高清图片（1080p / 2K / 4K；可选视角）">
        <Button size="small" icon={<PictureOutlined />} onClick={() => setOpen(true)}>
          导出图片
        </Button>
      </Tooltip>
      <Modal
        title="导出高清图片"
        open={open}
        onOk={() => void doExport()}
        okText={exporting ? '导出中…' : '导出'}
        confirmLoading={exporting}
        onCancel={() => setOpen(false)}
        destroyOnHidden
        width={420}
      >
        <Space orientation="vertical" style={{ width: '100%' }} size={12}>
          <div data-testid="export-preset-select">
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              分辨率
            </Typography.Text>
            <Select
              style={{ width: '100%' }}
              value={presetKey}
              onChange={setPresetKey}
              options={EXPORT_PRESETS.map((p) => ({ value: p.key, label: p.label }))}
            />
          </div>
          <div data-testid="export-view-select">
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              视角
            </Typography.Text>
            <Select
              style={{ width: '100%' }}
              value={viewKey}
              onChange={setViewKey}
              options={EXPORT_VIEWS.map((v) => ({ value: v.key, label: v.label }))}
            />
          </div>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              格式
            </Typography.Text>
            <Radio.Group value={format} onChange={(e) => setFormat(e.target.value as 'png' | 'jpeg')}>
              <Radio.Button value="png">PNG（无损）</Radio.Button>
              <Radio.Button value="jpeg">JPEG（更小）</Radio.Button>
            </Radio.Group>
          </div>
          {watermarkFlag ? (
            <Typography.Text type="warning" style={{ fontSize: 12 }}>
              当前套餐导出将自动添加水印（升级/加购去水印后自动移除）。
            </Typography.Text>
          ) : null}
        </Space>
      </Modal>
    </>
  );
}
